import {
  AllowStringForNumbers,
  RecursivePartialSiteAllowStringValues,
  SiteAllowStringValues,
  SiteElectricalSystem,
} from "@inrange/building-manager-api-client/models-site";
import { Tooltip } from "@inrange/theme-components";
import { Icons } from "@inrange/theme-components/icons";
import { sortBy } from "@inrange/utils";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import _ from "lodash";
import { useState } from "react";
import { Alert, Button, Table } from "react-bootstrap";
import BatteryTooltip from "./BatteryTooltip";
import { formatNumber } from "./utils";

const months = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const BatteryBrandIcons = {
  GivEnergy: Icons.givenergy,
  SolarEdge: Icons.solaredge,
  Dyness: Icons.dyness,
  Sungrow: Icons.sungrow,
  Huawei: Icons.huawei,
  Invinity: Icons.invinity,
};

const BatteryResults = ({
  site,
  esys,
  setSite,
  baseline,
  capacityDebugData,
  results,
  currencyCode,
  isOwnerOccupied,
}: {
  site: SiteAllowStringValues;
  esys: AllowStringForNumbers<SiteElectricalSystem>;
  setSite: (site: RecursivePartialSiteAllowStringValues) => void;
  baseline: any;
  capacityDebugData: any;
  results: any[];
  currencyCode: string;
  isOwnerOccupied: boolean;
}) => {
  const [showCapacityDebug, setShowCapacityDebug] = useState(false);

  const batterySpecForComparison = (spec: any) => {
    const result = { ...spec };
    delete result.modelNotes;
    for (const key of [
      "powerMw",
      "capacityMwh",
      "depthOfDischarge",
      "roundTripEfficiency",
    ]) {
      if (result[key] !== undefined) {
        result[key] = Number(result[key]);
      }
    }
    return result;
  };

  const columns = [
    {
      header: "Brand",
      id: "brand",
      accessorFn: (row: any) => row.battery[esys.batteryIdx].batterySpec,
      sortingFn: "sortFnBrand",
      sortDescFirst: false,
      cell: (info: any) => {
        const row = info.row.original;
        return (
          <>
            {BatteryBrandIcons[
              row.battery[esys.batteryIdx].batterySpec.brand
            ] && (
              <Tooltip
                text={`${row.battery[esys.batteryIdx].batterySpec.modelName} x ${row.battery[esys.batteryIdx].batterySpec.count}`}
                position="right"
              >
                <img
                  src={
                    BatteryBrandIcons[
                      row.battery[esys.batteryIdx].batterySpec.brand
                    ]
                  }
                  alt={row.brand}
                  style={{ width: "40px" }}
                />
              </Tooltip>
            )}
          </>
        );
      },
    },
    {
      header: "Power (MW)",
      id: "power_mw",
      accessorFn: (row: any) =>
        row.battery[esys.batteryIdx].batterySpec.powerMw *
        row.battery[esys.batteryIdx].batterySpec.count,
      sortingFn: "sortFnPower",
      sortDescFirst: false,
      cell: (info: any) => formatNumber(info.getValue(), 0, 6, false),
    },
    {
      header: "Capacity (MWh)",
      id: "capacity_mwh",
      accessorFn: (row: any) =>
        row.battery[esys.batteryIdx].batterySpec.capacityMwh *
        row.battery[esys.batteryIdx].batterySpec.count,
      sortingFn: "sortFnCapacity",
      sortDescFirst: false,
      cell: (info: any) => {
        const row = info.row.original;
        const usableChargeKwh =
          row.battery[esys.batteryIdx].batterySpec.capacityMwh *
          row.battery[esys.batteryIdx].batterySpec.count *
          row.battery[esys.batteryIdx].batterySpec.depthOfDischarge *
          1000.0;
        const annualCyclesPercent =
          (row.esys[esys.idx].energyFlowAnnual.batteryDischarge /
            usableChargeKwh /
            366) *
          100;
        return (
          <>
            {formatNumber(info.getValue(), 0, 6, false)}{" "}
            <span style={{ display: "inline-block" }}>
              (
              <BatteryTooltip
                batterySpec={row.battery[esys.batteryIdx].batterySpec}
                energyFlowAnnual={row.esys[esys.idx].energyFlowAnnual}
                energyFlowMonthly={row.esys[esys.idx].energyFlowMonthly}
              >
                <span
                  style={{
                    textDecoration: "dotted underline 1px",
                  }}
                >
                  {formatNumber(annualCyclesPercent, 0, 0, false)}%
                </span>
              </BatteryTooltip>
              )
            </span>
          </>
        );
      },
    },
    {
      header: `IRR (${isOwnerOccupied ? "OO" : "LL"}, %)`,
      id: "irr",
      accessorFn: (row: any) =>
        row.financialModels[isOwnerOccupied ? "ownerOccupier" : "landlord"]
          .license.irr,
      sortingFn: "sortIrr",
      sortDescFirst: true,
      cell: (info: any) => {
        const row = info.row.original;
        const ownershipKey = isOwnerOccupied ? "ownerOccupier" : "landlord";
        const updated = row.financialModels[ownershipKey].license.irr;
        const initial = baseline.financialModels[ownershipKey].license.irr || 0;
        const delta = updated - initial;
        return (
          <>
            {formatNumber(updated * 100, 0, 2, false)}% <br />(
            {formatNumber(delta * 100, 0, 2, false)})
          </>
        );
      },
    },
    {
      header: "Battery cost",
      id: "battery_cost",
      accessorFn: (row: any) => {
        const costKey =
          row.battery[esys.batteryIdx].batterySpec.modelName ?? "Custom";
        const usableChargeKwh =
          row.battery[esys.batteryIdx].batterySpec.capacityMwh *
          row.battery[esys.batteryIdx].batterySpec.count *
          row.battery[esys.batteryIdx].batterySpec.depthOfDischarge *
          1000.0;
        return (
          <>
            {formatCurrency(
              (row.costInputsBattery.costs[costKey].hardwareCost +
                row.costInputsBattery.costs[costKey].installationCost) *
                row.battery[esys.batteryIdx].batterySpec.count,
              currencyCode
            )}
            <br />
            {formatCurrency(
              ((row.costInputsBattery.costs[costKey].hardwareCost +
                row.costInputsBattery.costs[costKey].installationCost) *
                row.battery[esys.batteryIdx].batterySpec.count) /
                usableChargeKwh,
              currencyCode
            )}
            /kWh
          </>
        );
      },
      sortingFn: "sortFnBatteryCost",
      sortDescFirst: false,
      cell: (info: any) => info.getValue(),
    },
    {
      header: "Total fulfilled by on-site (%)",
      id: "tenant_demand_share",
      accessorFn: (row: any) => row,
      sortingFn: "sortFnTenantDemand",
      sortDescFirst: true,
      cell: (info: any) => {
        const row = info.row.original;
        const updated =
          row.esys[esys.idx].energyFlowAnnual.behindMeter /
          row.esys[esys.idx].energyFlowAnnual.demand;
        const initial =
          baseline.esys[esys.idx].energyFlowAnnual.behindMeter /
          baseline.esys[esys.idx].energyFlowAnnual.demand;
        const delta = updated - initial;
        return (
          <>
            {formatNumber(updated * 100, 0, 2, false)}% <br />(
            {formatNumber(delta * 100, 0, 2, false)})
          </>
        );
      },
    },
    {
      header: "Tenant energy share (%)",
      id: "tenant_energy_share",
      accessorFn: (row: any) => row,
      sortingFn: "sortFnTenantGeneration",
      sortDescFirst: true,
      cell: (info: any) => {
        const row = info.row.original;
        const updated =
          row.esys[esys.idx].energyFlowAnnual.generation > 0
            ? row.esys[esys.idx].energyFlowAnnual.behindMeter /
              row.esys[esys.idx].energyFlowAnnual.generation
            : 0;
        const initial =
          baseline.esys[esys.idx].energyFlowAnnual.generation > 0
            ? baseline.esys[esys.idx].energyFlowAnnual.behindMeter /
              baseline.esys[esys.idx].energyFlowAnnual.generation
            : 0;
        const delta = updated - initial;
        return (
          <>
            {formatNumber(updated * 100, 0, 2, false)}% <br />(
            {formatNumber(delta * 100, 0, 2, false)})
          </>
        );
      },
    },
    {
      header: "Export (kWh/yr)",
      id: "export_kwh",
      accessorFn: (row: any) => row,
      sortingFn: "sortFnExport",
      sortDescFirst: true,
      cell: (info: any) => {
        const row = info.row.original;
        const updated = row.esys[esys.idx].energyFlowAnnual.exported;
        const initial = baseline.esys[esys.idx].energyFlowAnnual.exported;
        const delta = updated - initial;
        return (
          <>
            {formatNumber(updated, 0, 0, true)} <br />(
            {formatNumber(delta, 0, 0, true)})
          </>
        );
      },
    },
    {
      header: "Curtailed (kWh/yr)",
      id: "curtailment_kwh",
      accessorFn: (row: any) => row,
      sortingFn: "sortFnCurtailed",
      sortDescFirst: true,
      cell: (info: any) => {
        const row = info.row.original;
        const updated = row.esys[esys.idx].energyFlowAnnual.curtailed;
        const initial = baseline.esys[esys.idx].energyFlowAnnual.curtailed;
        const delta = updated - initial;
        return (
          <>
            {formatNumber(updated, 0, 0, true)} <br />(
            {formatNumber(delta, 0, 0, true)})
          </>
        );
      },
    },
    {
      header: "",
      id: "actions",
      accessorFn: (row: any) => row,
      cell: ({ row }: { row: any }) => {
        const siteBatteryDetails = {
          battery: {
            [esys.batteryIdx]: {
              batterySpec: batterySpecForComparison(
                site.battery[esys.batteryIdx].batterySpec
              ),
              batteryEnergyFlowPayloadHash:
                site.battery[esys.batteryIdx].batteryEnergyFlowPayloadHash,
            },
          },
          costInputsBattery: {
            costs: {
              [site.battery[esys.batteryIdx].batterySpec?.modelName ??
              "Custom"]:
                site.costInputsBattery.costs[
                  site.battery[esys.batteryIdx].batterySpec?.modelName ??
                    "Custom"
                ],
            },
          },
        };
        const rowBatteryDetails = {
          battery: {
            [esys.batteryIdx]: {
              batterySpec: batterySpecForComparison(
                row.original.battery[esys.batteryIdx].batterySpec
              ),
              batteryEnergyFlowPayloadHash:
                row.original.battery[esys.batteryIdx]
                  .batteryEnergyFlowPayloadHash,
            },
          },
          costInputsBattery: row.original.costInputsBattery,
        };
        const disabled = _.isEqual(siteBatteryDetails, rowBatteryDetails);
        return (
          <Button
            variant="success"
            onClick={() => {
              setSite({
                battery: row.original.battery,
                costInputsBattery: row.original.costInputsBattery,
              });
            }}
            disabled={disabled}
            style={{ padding: "0.375rem", width: "100%", fontSize: "90%" }}
            size="sm"
          >
            {disabled ? "Selected" : "Select"}
          </Button>
        );
      },
      disableSortBy: true,
    },
  ];

  // Failed variants from BMA come through as `{failed: true, failureReason,
  // battery: {...}}` entries with no financial model / esys data. Split them
  // out and surface prominently above the results table so the user sees them
  // first (see ADMIN-APP-8J / BUILDING-MANAGER-API-120).
  const failedResults = results.filter((r) => r?.failed === true);
  const successfulResults = results.filter((r) => r?.failed !== true);

  return (
    <>
      {failedResults.length > 0 && (
        <Alert variant="warning" className="mb-3">
          <Alert.Heading style={{ fontSize: "1rem" }}>
            {failedResults.length === 1
              ? "1 battery configuration failed"
              : `${failedResults.length} battery configurations failed`}
          </Alert.Heading>
          <ul className="mb-0" style={{ fontSize: "0.875rem" }}>
            {failedResults.map((r, idx) => {
              const spec = r.battery?.[esys.batteryIdx]?.batterySpec ?? {};
              const label =
                spec.modelName && spec.count
                  ? `${spec.modelName} × ${spec.count}`
                  : spec.capacityMwh && spec.powerMw
                    ? `Synthetic ${Number(spec.capacityMwh).toFixed(3)} MWh / ${Number(spec.powerMw).toFixed(3)} MW`
                    : "Battery configuration";
              return (
                <li key={idx}>
                  <strong>{label}</strong>
                  {r.failureReason ? ` — ${r.failureReason}` : ""}
                </li>
              );
            })}
          </ul>
        </Alert>
      )}
      <ResultsTable
        esys={esys}
        columns={columns}
        data={successfulResults}
        isOwnerOccupied={isOwnerOccupied}
      />
      {!showCapacityDebug && (
        <a
          onClick={() => setShowCapacityDebug(true)}
          className="text-primary"
          style={{
            cursor: "pointer",
            textDecoration: "none",
          }}
        >
          Show capacity range information
        </a>
      )}
      {showCapacityDebug && (
        <>
          <p>
            Auto-detected capacity range (MWh):{" "}
            {capacityDebugData.range.join(", ")}
          </p>
          <Table striped bordered hover size="sm" responsive>
            <thead>
              <tr>
                <th>Month</th>
                <th>Avg day exportable generation (MWh)</th>
                <th>Avg day grid import (MWh)</th>
                <th>Max usable capacity (MWh)</th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 12 }).map((_, monthIndex) => (
                <tr key={monthIndex}>
                  <td>{months[monthIndex]}</td>
                  <td>
                    {capacityDebugData.monthlyInputs.excessGenerationMwh[
                      monthIndex
                    ].toFixed(3)}
                  </td>
                  <td>
                    {capacityDebugData.monthlyInputs.gridImportMwh[
                      monthIndex
                    ].toFixed(3)}
                  </td>
                  <td>
                    {capacityDebugData.monthlyInputs.maxUsableCapacityMwh[
                      monthIndex
                    ].toFixed(3)}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      )}
    </>
  );
};

const ResultsTable = ({
  esys,
  columns,
  data,
  isOwnerOccupied,
}: {
  esys: AllowStringForNumbers<SiteElectricalSystem>;
  columns: any[];
  data: any[];
  isOwnerOccupied: boolean;
}) => {
  const [sorting, setSorting] = useState([
    { id: columns[3].id ?? "", desc: true },
  ]);
  const table = useReactTable({
    data,
    columns,
    sortingFns: {
      sortFnBrand: sortBy(
        (row: any) => row.original.battery[esys.batteryIdx].batterySpec.brand
      ),
      sortFnPower: sortBy(
        (row: any) => row.original.battery[esys.batteryIdx].batterySpec.powerMw
      ),
      sortFnCapacity: sortBy(
        (row: any) =>
          row.original.battery[esys.batteryIdx].batterySpec.capacityMwh *
          row.original.battery[esys.batteryIdx].batterySpec.count
      ),
      sortIrr: sortBy(
        (row: any) =>
          row.original.financialModels[
            isOwnerOccupied ? "ownerOccupier" : "landlord"
          ].license.irr
      ),
      sortFnBatteryCost: sortBy(
        (row: any) =>
          row.original.battery[esys.batteryIdx].batterySpec.hardwareCost
      ),
      sortFnTenantGeneration: sortBy(
        (row: any) =>
          row.original.esys[esys.idx].energyFlowAnnual.behindMeter /
          row.original.esys[esys.idx].energyFlowAnnual.generation
      ),
      sortFnTenantDemand: sortBy(
        (row: any) =>
          row.original.esys[esys.idx].energyFlowAnnual.behindMeter /
          row.original.esys[esys.idx].energyFlowAnnual.demand
      ),
      sortFnExport: sortBy(
        (row: any) => row.original.esys[esys.idx].energyFlowAnnual.exported
      ),
      sortFnCurtailed: sortBy(
        (row: any) => row.original.esys[esys.idx].energyFlowAnnual.curtailed
      ),
    },
    state: {
      sorting,
    },
    enableSortingRemoval: false,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <Table
      striped
      bordered
      hover
      responsive
      style={{
        fontSize: "80%",
        height: "600px",
        overflow: "scroll",
        display: "block",
        border: "1px solid #dee2e6",
      }}
      id="battery-results-table"
    >
      <thead>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <th
                key={header.id}
                style={{ cursor: "pointer", padding: "0.3rem" }}
                onClick={header.column.getToggleSortingHandler()}
              >
                <div style={{ display: "flex" }}>
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                  <SortArrow isSorted={header.column.getIsSorted()} />
                </div>
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map((row) => (
          <tr key={row.id}>
            {row.getVisibleCells().map((cell) => (
              <td
                key={cell.id}
                style={{
                  padding: "0.3rem",
                  backgroundColor: cell.row.original.battery[esys.batteryIdx]
                    .batterySpec.modelName
                    ? "#e6f3ff"
                    : "transparent",
                }}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

const SortArrow = ({ isSorted }: { isSorted: false | "asc" | "desc" }) => {
  if (!isSorted) {
    return (
      <span
        style={{
          width: 8,
          display: "inline-block",
        }}
      >
        &nbsp;
      </span>
    );
  }
  const arrow = isSorted === "desc" ? Icons.sortDesc : Icons.sortAsc;

  return <img alt="view" src={arrow} />;
};

const formatCurrency = (value: number, currencyCode: string) => {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: currencyCode,
    maximumFractionDigits: 0,
  }).format(value);
};

export default BatteryResults;
